/**
 * Preguntas frecuentes de una ficha de producto, armadas SOLO con datos que
 * el catálogo ya publica (gramaje, medida, peso, precio) y con las mismas
 * respuestas de envío y mayoreo que el FAQ del catálogo.
 *
 * Por qué existe: la ficha tenía ~270 palabras y ningún subtítulo, y es la
 * página más cercana a la compra. Un buscador, y sobre todo un asistente de
 * IA, responde "¿cuánto pesa la paca de papel oro?" extrayendo un pasaje que
 * conteste esa pregunta tal cual. Aquí cada pregunta se responde en una o dos
 * frases autocontenidas.
 *
 * REGLAS (las mismas que el resto del sitio):
 *   · Nada que no esté en el catálogo: sin certificaciones, sin existencias,
 *     sin tiempos de entrega, sin "el más vendido".
 *   · Un dato ausente NO se inventa ni se imprime como 0: hay productos con
 *     `weight_grams: 0` (bolsas) y medidas que son numeración ("0", "1"), y
 *     "0 g" o "0 cm" serían falsos. La pregunta simplemente no se genera.
 *   · El texto visible y el FAQPage schema salen de ESTA misma lista: Google
 *     trata un schema que no coincide con lo que se ve como riesgo de acción
 *     manual.
 */

// Misma convención que ProductCard y generate-llms.js: el precio de las
// bolsas es por kilo, el de los snacks por millar, el resto por paca.
export function unidadDeVenta(product) {
    if (product.category === 'bolsas_poliseda') return 'kilo';
    if (product.category === 'papel_snacks') return 'millar';
    return 'paca';
}

const miles = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// "34x46" y "30×40" conviven en el catálogo: se normaliza a "34×46".
const medidaLegible = (m) => String(m).replace(/\s*[xX×]\s*/, '×');

// "cm" SOLO cuando la medida es una dimensión real. `sizes` también guarda
// numeración de bolsa ("0", "1", "2"); "0 cm" sería un dato falso.
const esDimension = (m) => /\d\s*[x×]\s*\d/i.test(m);

export function medidasTexto(product) {
    const sizes = Array.isArray(product.sizes) ? product.sizes.filter(Boolean) : [];
    if (!sizes.length) return null;
    const lista = sizes.map(medidaLegible);
    const unir = (xs) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}` : xs[0]);
    return sizes.every(esDimension)
        ? { texto: `${unir(lista)} cm`, plural: lista.length > 1 }
        : { texto: unir(lista), plural: lista.length > 1, numeracion: true };
}

export function productFaq(product, sector) {
    const name = product.name;
    const unidad = unidadDeVenta(product);
    const faq = [];

    if (product.weight_grams > 0) {
        faq.push({
            q: `¿Qué gramaje tiene ${name}?`,
            a: `${name} es de ${product.weight_grams} g. Como referencia, en el catálogo de Carpepel los papeles de 16–18 g son para productos ligeros y secos, los de 24–30 g para uso general (tacos, tortas, antojitos) y los de 40–49 g para producto pesado, húmedo o muy grasoso.`,
        });
    }

    const medidas = medidasTexto(product);
    if (medidas) {
        faq.push({
            q: `¿En qué medidas se vende ${name}?`,
            a: medidas.numeracion
                ? `${name} se vende en ${medidas.plural ? 'las presentaciones' : 'la presentación'} ${medidas.texto}.`
                : `${name} se vende en ${medidas.plural ? 'las medidas' : 'la medida'} ${medidas.texto}.`,
        });
    }

    if (unidad === 'paca' && product.package_weight_kg > 0) {
        faq.push({
            q: `¿Cuánto pesa la paca de ${name}?`,
            a: `Cada paca de ${name} pesa ${product.package_weight_kg} kg.`,
        });
    }

    if (typeof product.price === 'number' && product.price > 0) {
        const precio = `$${miles(product.price)} MXN`;
        const por = unidad === 'kilo'
            ? `${precio} por kilogramo`
            : unidad === 'millar'
                ? `${precio} por millar (1,000 hojas)`
                : `${precio} por paca${product.package_weight_kg > 0 ? ` de ${product.package_weight_kg} kg` : ''}`;
        faq.push({
            q: `¿Cuánto cuesta ${name}?`,
            a: `${name} cuesta ${por}. El precio vigente es el que aparece en esta página y puede cambiar sin previo aviso.`,
        });
    }

    faq.push({
        q: `¿${name} se vende al mayoreo y al menudeo?`,
        a: 'Sí, Carpepel vende tanto al mayoreo como al menudeo, con precios especiales por volumen para negocios.',
    });

    faq.push({
        q: `¿Hacen envíos de ${name}?`,
        a: 'Sí, Carpepel envía a toda la República Mexicana. Puedes pagar solo el papel y coordinar el envío por WhatsApp, donde se cotiza y se cobra aparte, o cotizarlo con tu código postal y pagar todo junto. Para envíos a Estados Unidos, Canadá o Centroamérica, la cotización se hace por WhatsApp.',
    });

    if (sector) {
        faq.push({
            q: `¿Para qué tipo de negocio sirve ${name}?`,
            a: `Por su gramaje y presentación, ${name} es de los papeles de Carpepel que sirven a ${sector.label.toLowerCase()}. Encuentras todo el papel para ese giro en la página del sector.`,
        });
    }

    return faq;
}

/** FAQPage para el JSON-LD, a partir de la misma lista que se ve en pantalla. */
export function faqSchema(items) {
    return {
        '@type': 'FAQPage',
        mainEntity: items.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
        })),
    };
}
